import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Nav from "@/components/welp/Nav";
import PageBg, { POSTER_P4_ZODIAC } from "@/components/welp/PageBg";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles, Stars, Heart, Download, Share2 } from "lucide-react";
import { welpPdf } from "@/lib/welpDoc";

function natalToDoc(d) {
  const houses = (d.computed?.houses || []).map(h => {
    const interp = d.houses_interpretation?.[String(h.house)];
    return `House ${h.house} — ${h.sign}${interp ? `\n${interp}` : ""}`;
  }).join("\n");
  const patterns = (d.special_patterns || []).map(pt => `${pt.name}\n${pt.meaning}`).join("\n\n");
  welpPdf("My Natal Chart", [
    { heading: "Sun", text: d.sun },
    { heading: "Moon", text: d.moon },
    { heading: "Rising", text: d.rising },
    { heading: "North Node", text: d.north_node },
    { heading: "South Node", text: d.south_node },
    { heading: "Chiron — The Wounded Healer", text: d.chiron },
    patterns ? { heading: "Special Chart Patterns", text: patterns } : null,
    { heading: "Summary", text: d.summary },
    { heading: "Strengths", items: d.strengths },
    { heading: "Growth Edges", items: d.growth_edges },
    { heading: "In Relationships", text: d.relationships },
    d.aspects_interpretation ? { heading: "Aspects", text: d.aspects_interpretation } : null,
    houses ? { heading: "Houses (Placidus)", text: houses } : null,
    { heading: "Self-Awareness Prompts", items: d.self_awareness_prompts },
  ], "Welp-Natal-Chart.pdf");
}

function synastryToDoc(d) {
  welpPdf("Our Synastry Reading", [
    { heading: `Overall Score — ${d.score}/100`, text: d.headline },
    { heading: "Attraction", text: d.attraction },
    { heading: "Communication", text: d.communication },
    { heading: "Friction", text: d.friction },
    { heading: "Long-Term", text: d.long_term },
    { heading: "Advice", items: d.advice },
  ], "Welp-Synastry-Reading.pdf");
}

function tarotToDoc(d) {
  welpPdf("Tarot Reading", [
    { heading: "Your Question", text: d.question },
    ...(d.cards || []).map((c, i) => ({
      heading: `${c.position} — ${c.name} (${c.orientation})`,
      text: d.card_readings?.[i] || "",
    })),
    { heading: "The Cards' Answer", text: d.overall },
    { heading: "Guidance", items: d.guidance },
  ], "Welp-Tarot-Reading.pdf");
}

function DownloadBtn({ onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-[#FF1A82] bg-white/70 text-[#1A1A1A] font-bold text-sm hover:bg-[#FF1A82]/10 transition-colors">
      <Download className="w-4 h-4 text-[#FF1A82]" /> Download
    </button>
  );
}

function Tab({ active, onClick, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-5 py-3 rounded-full font-bold text-base transition ${active ? "bg-[#FF1A82] text-white" : "bg-white/70 backdrop-blur-lg text-[#1A1A1A] border-2 border-black/10 hover:border-[#FF1A82]"}`}>
      {label}
    </button>
  );
}

function Bullets({ items, color = "#FF1A82" }) {
  return (
    <ul className="space-y-2">
      {(items || []).map((b, i) => (
        <li key={`${i}-${typeof b === "string" ? b.slice(0, 32) : i}`} className="flex items-start gap-2 text-[#1A1A1A] font-semibold">
          <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} />
          <span>{b}</span>
        </li>
      ))}
    </ul>
  );
}

function PlacePicker({ value, lat, lon, onPick }) {
  const [q, setQ] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [locked, setLocked] = useState(!!(lat && lon));

  useEffect(() => { setQ(value || ""); setLocked(!!(lat != null && lon != null)); }, [value, lat, lon]);

  useEffect(() => {
    if (locked) return;
    const s = q.trim();
    if (s.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        setBusy(true);
        const r = await api.placeSearch(s);
        setResults(r.results || []);
        setOpen(true);
      } catch (_) { setResults([]); }
      finally { setBusy(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [q, locked]);

  const pick = (row) => {
    setQ(row.label);
    setResults([]);
    setOpen(false);
    setLocked(true);
    onPick(row);
  };

  const clear = () => {
    setLocked(false);
    setQ("");
    setResults([]);
    setOpen(false);
    onPick(null);
  };

  return (
    <div className="relative">
      <input
        value={q}
        onChange={e => { setQ(e.target.value); setLocked(false); }}
        onFocus={() => { if (results.length) setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        placeholder="Type a city — pick from the list"
        data-testid="natal-place"
        readOnly={locked}
        className={`mt-2 w-full bg-white/70 backdrop-blur-lg border-2 rounded-xl px-4 py-3 text-[#1A1A1A] outline-none focus:border-[#FF1A82] font-bold ${locked ? 'border-[#FF1A82] bg-[#FF1A82]/10' : 'border-black/15'}`}
      />
      {locked && (
        <button type="button" onClick={clear} data-testid="natal-place-clear"
          className="absolute right-3 top-1/2 mt-1 -translate-y-1/2 text-xs font-bold text-[#10B981] hover:underline">
          Change
        </button>
      )}
      {open && !locked && (results.length > 0 || busy) && (
        <div className="absolute z-30 left-0 right-0 mt-1 rounded-xl bg-white/70 backdrop-blur-lg border-2 border-[#FF1A82] shadow-xl overflow-hidden">
          {busy && <div className="px-4 py-3 text-sm text-[#5A5A5A] font-semibold">Searching…</div>}
          {results.map((r, i) => (
            <button key={`${r.lat}-${r.lon}-${i}`} type="button" onMouseDown={(e) => { e.preventDefault(); pick(r); }}
              data-testid={`place-option-${i}`}
              className="w-full text-left px-4 py-3 hover:bg-[#FF1A82]/10 border-b border-black/10 last:border-0">
              <div className="font-bold text-[#1A1A1A]">{r.label}</div>
              <div className="text-xs text-[#5A5A5A] font-semibold">{r.lat.toFixed(3)}, {r.lon.toFixed(3)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function NatalView() {
  const [date, setDate] = useState("");
  const [hour12, setHour12] = useState("");    // "01".."12"
  const [minute, setMinute] = useState("");    // "00".."59"
  const [ampm, setAmpm] = useState("AM");
  const [place, setPlace] = useState("");
  const [lat, setLat] = useState(null);
  const [lon, setLon] = useState(null);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [savedChart, setSavedChart] = useState(null);
  const [forSelf, setForSelf] = useState(true);

  const hasTime = hour12 && minute;

  const applyChart = (c) => {
    setDate(c.birth_date || "");
    setPlace(c.birth_place || ""); setLat(c.birth_lat ?? null); setLon(c.birth_lon ?? null);
    if (c.birth_time) {
      const [hs, ms] = c.birth_time.split(":");
      const h24 = parseInt(hs, 10);
      setAmpm(h24 >= 12 ? "PM" : "AM");
      setHour12(String(h24 % 12 === 0 ? 12 : h24 % 12).padStart(2, "0"));
      setMinute(ms);
    } else { setHour12(""); setMinute(""); }
  };

  const clearForm = () => {
    setDate(""); setPlace(""); setLat(null); setLon(null);
    setHour12(""); setMinute(""); setAmpm("AM"); setData(null);
  };

  useEffect(() => {
    api.myNatalChart().then(r => {
      const c = r.chart;
      if (!c) return;
      setSavedChart(c);
      applyChart(c);
      toast.info("Loaded your saved birth details.");
    }).catch(() => {});
  }, []); // eslint-disable-line

  const run = async () => {
    if (!date) { toast.error("Birth date is required."); return; }
    if (!(place || "").trim()) { toast.error("Please enter a birth place (city or region)."); return; }

    // Build 24h HH:MM if the user picked a time
    let birth_time = null;
    if (hasTime) {
      let h = parseInt(hour12, 10);
      if (ampm === "AM") h = (h === 12 ? 0 : h);
      else               h = (h === 12 ? 12 : h + 12);
      birth_time = `${String(h).padStart(2, "0")}:${minute}`;
    }

    setBusy(true); setData(null);
    try {
      const start = await api.natalChart({
        birth_date: date, birth_time, birth_place: place,
        // Backend will auto-geocode if lat/lon are null
        birth_lat: lat, birth_lon: lon,
        for_self: forSelf,
      });
      // Deep readings run as a background job — poll until it's done (up to ~5 min)
      let result = null;
      for (let i = 0; i < 60; i++) {
        await new Promise(res => setTimeout(res, 5000));
        const j = await api.natalJob(start.job_id);
        if (j.status === "done") { result = j.result; break; }
        if (j.status === "error") throw new Error(j.detail || "Couldn't generate chart.");
      }
      if (!result) throw new Error("The stars are taking unusually long — please try again.");
      setData(result);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Couldn't generate chart.");
    } finally { setBusy(false); }
  };

  const hours = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
  const minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

  return (
    <div data-testid="natal-chart" className="text-veil">
      <p className="text-[#1A1A1A] font-semibold mb-5">A natal chart shows where every planet was at the moment of birth. You can run your own chart — or look up anyone else's without touching your saved details. Birth time isn&apos;t required, but it&apos;s needed for an accurate Rising sign.</p>

      <div className="mb-6 inline-flex rounded-2xl border-2 border-black/15 overflow-hidden" data-testid="natal-for-toggle">
        <button type="button" data-testid="natal-for-me"
          onClick={() => { setForSelf(true); if (savedChart) applyChart(savedChart); setData(null); }}
          className={`px-6 py-3 font-bold text-lg ${forSelf ? "bg-[#FF1A82] text-white" : "bg-white/70 backdrop-blur-lg text-[#5A5A5A]"}`}>My chart</button>
        <button type="button" data-testid="natal-for-other"
          onClick={() => { setForSelf(false); clearForm(); }}
          className={`px-6 py-3 font-bold text-lg ${!forSelf ? "bg-[#10B981] text-white" : "bg-white/70 backdrop-blur-lg text-[#5A5A5A]"}`}>Someone else's</button>
      </div>
      {!forSelf && (
        <p className="mb-5 text-lg text-[#1A1A1A] font-semibold rounded-2xl bg-[#10B981]/10 border-2 border-[#10B981]/40 px-5 py-3" data-testid="natal-other-note">
          Looking up someone else — enter <strong>their</strong> birth details. Your own saved birth info stays untouched.
        </p>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-[0.22em] text-[#FF1A82] font-bold">Birth date</label>
          <input type="date" required value={date} onChange={e => setDate(e.target.value)} data-testid="natal-date"
            className="mt-2 w-full bg-white/70 backdrop-blur-lg border border-black/10 rounded-2xl px-4 py-3 text-[#1A1A1A] outline-none focus:border-[#FF1A82] font-bold" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.22em] text-[#FF1A82] font-bold">Birth place</label>
          <PlacePicker value={place} lat={lat} lon={lon} onPick={(row) => {
            if (!row) { setPlace(""); setLat(null); setLon(null); return; }
            setPlace(row.label); setLat(row.lat); setLon(row.lon);
          }} />
        </div>
      </div>

      <div className="mt-4">
        <label className="text-xs uppercase tracking-[0.22em] text-[#FF1A82] font-bold">Birth time (optional — for accurate Rising sign)</label>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <select value={hour12} onChange={e => setHour12(e.target.value)} data-testid="natal-hour"
            className="bg-white/70 backdrop-blur-lg border border-black/10 rounded-2xl px-4 py-3 text-[#1A1A1A] outline-none focus:border-[#FF1A82] font-bold">
            <option value="">Hour</option>
            {hours.map(h => <option key={h} value={h}>{h}</option>)}
          </select>
          <span className="font-bold text-2xl text-[#5A5A5A]">:</span>
          <select value={minute} onChange={e => setMinute(e.target.value)} data-testid="natal-minute"
            className="bg-white/70 backdrop-blur-lg border border-black/10 rounded-2xl px-4 py-3 text-[#1A1A1A] outline-none focus:border-[#FF1A82] font-bold">
            <option value="">Min</option>
            {minutes.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <div className="inline-flex rounded-xl border-2 border-black/15 overflow-hidden ml-2">
            <button type="button" onClick={() => setAmpm("AM")} data-testid="natal-am"
              className={`px-5 py-3 font-bold ${ampm === "AM" ? "bg-[#FF1A82] text-white" : "bg-white/70 backdrop-blur-lg text-[#5A5A5A]"}`}>AM</button>
            <button type="button" onClick={() => setAmpm("PM")} data-testid="natal-pm"
              className={`px-5 py-3 font-bold ${ampm === "PM" ? "bg-[#FF1A82] text-white" : "bg-white/70 backdrop-blur-lg text-[#5A5A5A]"}`}>PM</button>
          </div>
          {hasTime && (
            <button type="button" onClick={() => { setHour12(""); setMinute(""); }} data-testid="natal-time-clear"
              className="ml-2 text-xs font-bold text-[#10B981] hover:underline">Clear time</button>
          )}
        </div>
      </div>

      <button onClick={run} disabled={busy} data-testid="natal-run"
        className="mt-6 px-7 py-4 rounded-full bg-[#FF1A82] text-white font-bold text-lg inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-60">
        <Stars className="w-5 h-5" /> {busy ? "Reading the chart…" : forSelf ? "Generate my chart" : "Generate their chart"}
      </button>
      {busy && (
        <p className="mt-3 text-lg font-bold text-[#10B981]" data-testid="natal-loading-notice">
          ✨ The stars are aligning — this can take up to a minute. Please stay on this page.
        </p>
      )}
      {!data && savedChart?.reading && (
        <span className="ml-3 inline-block align-middle">
          <DownloadBtn testid="natal-download-saved"
            onClick={() => natalToDoc({ ...savedChart.reading, computed: savedChart.computed })} />
        </span>
      )}

      {data && (
        <div className="mt-10 space-y-6" data-testid="natal-result">
          <div className="flex items-center gap-3 flex-wrap">
          {data.auto_saved && (
            <div className="rounded-full inline-flex items-center gap-2 bg-[#FF1A82]/10 border-2 border-[#FF1A82] px-4 py-2 text-sm font-bold text-[#1A1A1A]" data-testid="natal-autosaved">
              <Stars className="w-4 h-4 text-[#FF1A82]" /> Saved to your profile
            </div>
          )}
          <DownloadBtn testid="natal-download" onClick={() => natalToDoc(data)} />
          </div><div className="grid md:grid-cols-3 gap-5">
            <div className="rounded-2xl bg-white/70 backdrop-blur-lg border-2 border-[#FF1A82] p-5 shadow-sm">
              <div className="text-xs uppercase tracking-[0.22em] text-[#FF1A82] font-bold">Sun</div>
              <p className="mt-2 text-[#1A1A1A] font-semibold">{data.sun}</p>
            </div>
            <div className="rounded-2xl bg-white/70 backdrop-blur-lg border-2 border-[#FF1A82] p-5 shadow-sm">
              <div className="text-xs uppercase tracking-[0.22em] text-[#FF1A82] font-bold">Moon</div>
              <p className="mt-2 text-[#1A1A1A] font-semibold">{data.moon}</p>
            </div>
            <div className="rounded-2xl bg-white/70 backdrop-blur-lg border-2 border-[#FF1A82] p-5 shadow-sm">
              <div className="text-xs uppercase tracking-[0.22em] text-[#FF1A82] font-bold">Rising</div>
              <p className="mt-2 text-[#1A1A1A] font-semibold">{data.rising}</p>
            </div>
          </div>

          {(data.north_node || data.south_node || data.chiron) && (
            <div className="grid md:grid-cols-3 gap-5" data-testid="natal-nodes">
              {data.north_node && (
                <div className="rounded-2xl bg-white/70 backdrop-blur-lg border-2 border-[#10B981] p-5 shadow-sm">
                  <div className="text-xs uppercase tracking-[0.22em] text-[#10B981] font-bold">North Node · where you're headed</div>
                  <p className="mt-2 text-[#1A1A1A] font-semibold">{data.north_node}</p>
                </div>
              )}
              {data.south_node && (
                <div className="rounded-2xl bg-white/70 backdrop-blur-lg border-2 border-[#10B981] p-5 shadow-sm">
                  <div className="text-xs uppercase tracking-[0.22em] text-[#10B981] font-bold">South Node · what you're releasing</div>
                  <p className="mt-2 text-[#1A1A1A] font-semibold">{data.south_node}</p>
                </div>
              )}
              {data.chiron && (
                <div className="rounded-2xl bg-white/70 backdrop-blur-lg border-2 border-[#10B981] p-5 shadow-sm">
                  <div className="text-xs uppercase tracking-[0.22em] text-[#10B981] font-bold">Chiron · the wounded healer</div>
                  <p className="mt-2 text-[#1A1A1A] font-semibold">{data.chiron}</p>
                </div>
              )}
            </div>
          )}

          {data.special_patterns?.length > 0 && (
            <div className="rounded-2xl bg-white/70 backdrop-blur-lg border-2 border-[#FF1A82] p-6 shadow-sm" data-testid="natal-patterns">
              <h3 className="font-heading text-2xl font-bold text-[#1A1A1A] flex items-center gap-2"><Sparkles className="w-6 h-6 text-[#10B981]" /> Special chart patterns</h3>
              <p className="mt-1 text-lg text-[#5A5A5A] font-semibold">Rare configurations that make this chart one of a kind.</p>
              <div className="mt-4 space-y-4">
                {data.special_patterns.map((pt, i) => (
                  <div key={i} className="rounded-xl bg-[#10B981]/5 border border-[#10B981]/30 px-4 py-3">
                    <div className="font-heading text-xl font-bold text-[#10B981]">{pt.name}</div>
                    <p className="mt-1 text-[#1A1A1A] font-semibold leading-relaxed">{pt.meaning}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.computed?.houses && (
            <div className="rounded-2xl bg-white/70 backdrop-blur-lg border-2 border-[#10B981]/40 p-6 shadow-sm" data-testid="natal-houses">
              <div className="flex items-baseline justify-between flex-wrap gap-3">
                <h3 className="font-heading text-xl font-bold text-[#1A1A1A]">Houses</h3>
                <span className="text-xs text-[#5A5A5A] font-semibold">Placidus system</span>
              </div>
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                {data.computed.houses.map(h => (
                  <div key={h.house} className="bg-white/70 rounded-xl px-4 py-3 border border-[#10B981]/30">
                    <div className="text-xs uppercase tracking-widest font-bold text-[#10B981]">House {h.house}</div>
                    <div className="font-heading text-xl font-bold text-[#1A1A1A]">{h.sign}</div>
                    {data.houses_interpretation?.[String(h.house)] && (
                      <p className="mt-2 text-[#333] font-semibold leading-relaxed">{data.houses_interpretation[String(h.house)]}</p>
                    )}
                  </div>
                ))}
              </div>
              {data.computed.midheaven && (
                <div className="mt-4 text-lg font-semibold text-[#1A1A1A]">
                  <span className="uppercase tracking-widest text-lg font-bold text-[#10B981] mr-2">Midheaven (MC)</span>
                  {data.computed.midheaven.sign}
                  <p className="mt-2 text-lg text-[#1A1A1A] font-semibold leading-relaxed">
                    Your Midheaven is the very top of your chart — the career you're called to, the reputation you build, and the "you" the world sees when you walk in the room. In {data.computed.midheaven.sign}, it colors how you shine publicly and the legacy you're here to leave.
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="rounded-2xl bg-white/70 backdrop-blur-lg border border-black/10 p-6 shadow-sm">
            <h3 className="font-heading text-2xl font-bold text-[#1A1A1A]">Your themes</h3>
            <p className="mt-3 text-[#1A1A1A] font-semibold leading-relaxed whitespace-pre-line">{data.summary}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="rounded-2xl bg-white/70 backdrop-blur-lg border border-black/10 p-6 shadow-sm">
              <h3 className="font-heading text-xl font-bold text-[#FF1A82]">Strengths</h3>
              <div className="mt-3"><Bullets items={data.strengths} /></div>
            </div>
            <div className="rounded-2xl bg-white/70 backdrop-blur-lg border border-black/10 p-6 shadow-sm">
              <h3 className="font-heading text-xl font-bold text-[#10B981]">Growth edges</h3>
              <div className="mt-3"><Bullets items={data.growth_edges} color="#10B981" /></div>
            </div>
          </div>
          <div className="rounded-2xl bg-white/70 backdrop-blur-lg border-2 border-[#FF1A82] p-6 shadow-sm">
            <h3 className="font-heading text-xl font-bold text-[#1A1A1A]">In relationships</h3>
            <p className="mt-3 text-[#1A1A1A] font-semibold leading-relaxed">{data.relationships}</p>
          </div>
          <div className="rounded-2xl bg-white/70 backdrop-blur-lg border border-black/10 p-6 shadow-sm">
            <h3 className="font-heading text-xl font-bold text-[#1A1A1A]">Questions for self-reflection</h3>
            <div className="mt-3"><Bullets items={data.self_awareness_prompts} /></div>
          </div>
        </div>
      )}
    </div>
  );
}

function SynastryPersonCard({ label, testidPrefix, data, setData, canSave, onSave, savedCharts = [], onPickSaved }) {
  const hasTime = data.hour && data.minute;
  return (
    <div className="rounded-3xl bg-white/70 backdrop-blur-lg border-2 border-[#FF1A82]/30 p-6 shadow-sm space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#FF1A82]">{label}</div>
        {savedCharts.length > 0 && (
          <select onChange={e => { const id = e.target.value; if (id) onPickSaved?.(id); e.target.value = ""; }}
            data-testid={`${testidPrefix}-use-saved`}
            className="text-xs bg-white/70 backdrop-blur-lg border-2 border-[#FF1A82]/40 rounded-lg px-2 py-1 font-bold text-[#1A1A1A]">
            <option value="">Use saved person…</option>
            {savedCharts.map(c => <option key={c.chart_id} value={c.chart_id}>{c.label}</option>)}
          </select>
        )}
      </div>
      <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#FF1A82]">Nickname (optional)</div>
      <input value={data.customLabel} onChange={e => setData({ ...data, customLabel: e.target.value })}
        placeholder="Nickname (optional)" data-testid={`${testidPrefix}-label`}
        className="w-full bg-black/5 border-2 border-black/10 rounded-xl px-4 py-3 text-[#1A1A1A] font-semibold outline-none focus:border-[#FF1A82]" />
      <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#FF1A82]">Birthday</div>
      <input type="date" value={data.date} onChange={e => setData({ ...data, date: e.target.value })}
        data-testid={`${testidPrefix}-date`}
        className="w-full bg-black/5 border-2 border-black/10 rounded-xl px-4 py-3 text-[#1A1A1A] font-bold outline-none focus:border-[#FF1A82]" />
      <div>
        <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#FF1A82]">Birth place</div>
        <PlacePicker value={data.place} lat={data.lat} lon={data.lon} onPick={row => {
          if (!row) { setData({ ...data, place: "", lat: null, lon: null }); return; }
          setData({ ...data, place: row.label, lat: row.lat, lon: row.lon });
        }} />
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#FF1A82] mb-1">Birth time (optional)</div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={data.hour} onChange={e => setData({ ...data, hour: e.target.value })} data-testid={`${testidPrefix}-hour`}
            className="bg-white/70 backdrop-blur-lg border border-black/10 rounded-2xl px-3 py-2 text-[#1A1A1A] font-bold outline-none focus:border-[#FF1A82]">
            <option value="">Hr</option>
            {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0")).map(h => <option key={h} value={h}>{h}</option>)}
          </select>
          <span className="font-bold">:</span>
          <select value={data.minute} onChange={e => setData({ ...data, minute: e.target.value })} data-testid={`${testidPrefix}-minute`}
            className="bg-white/70 backdrop-blur-lg border border-black/10 rounded-2xl px-3 py-2 text-[#1A1A1A] font-bold outline-none focus:border-[#FF1A82]">
            <option value="">Min</option>
            {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0")).map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <div className="inline-flex rounded-xl border-2 border-black/15 overflow-hidden">
            <button type="button" onClick={() => setData({ ...data, ampm: "AM" })} data-testid={`${testidPrefix}-am`}
              className={`px-3 py-2 font-bold ${data.ampm === "AM" ? "bg-[#FF1A82] text-white" : "bg-white/70 backdrop-blur-lg text-[#5A5A5A]"}`}>AM</button>
            <button type="button" onClick={() => setData({ ...data, ampm: "PM" })} data-testid={`${testidPrefix}-pm`}
              className={`px-3 py-2 font-bold ${data.ampm === "PM" ? "bg-[#FF1A82] text-white" : "bg-white/70 backdrop-blur-lg text-[#5A5A5A]"}`}>PM</button>
          </div>
          {hasTime && (
            <button type="button" onClick={() => setData({ ...data, hour: "", minute: "" })}
              className="text-xs font-bold text-[#10B981] hover:underline">Clear</button>
          )}
        </div>
      </div>
      {onSave && (
        <button type="button" onClick={onSave} disabled={!canSave} data-testid={`${testidPrefix}-save`}
          className="w-full mt-2 px-4 py-3 rounded-full bg-white/70 backdrop-blur-lg border-2 border-[#FF1A82] text-[#1A1A1A] font-bold hover:bg-[#FF1A82]/10 disabled:opacity-50 disabled:cursor-not-allowed">
          {canSave ? `Save ${(data.customLabel || label).slice(0, 24)}` : "Fill nickname + date + place to save"}
        </button>
      )}
    </div>
  );
}

function synastryPersonToPayload(p, defaultLabel) {
  let birth_time = null;
  if (p.hour && p.minute) {
    let h = parseInt(p.hour, 10);
    if (p.ampm === "AM") h = (h === 12 ? 0 : h);
    else                 h = (h === 12 ? 12 : h + 12);
    birth_time = `${String(h).padStart(2, "0")}:${p.minute}`;
  }
  return {
    label: (p.customLabel || defaultLabel).slice(0, 60),
    birth_date: p.date,
    birth_time,
    birth_place: p.place,
    birth_lat: p.lat,
    birth_lon: p.lon,
  };
}

function SynastryView() {
  const blank = { customLabel: "", date: "", place: "", lat: null, lon: null, hour: "", minute: "", ampm: "AM" };
  const [you, setYou] = useState(blank);
  const [them, setThem] = useState(blank);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState([]);

  useEffect(() => {
    api.listSavedCharts().then(r => setSaved(r.charts || [])).catch(() => {});
    api.myNatalChart().then(r => {
      if (r.chart) { applyChartToForm({ label: "You", ...r.chart }, setYou); toast.info("Your saved birth details were loaded into \"You\"."); }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ready = you.date && (you.lat != null && you.lon != null ? true : !!(you.place || "").trim())
             && them.date && (them.lat != null && them.lon != null ? true : !!(them.place || "").trim());

  const applyChartToForm = (chart, setter) => {
    let hour = "", minute = "", ampm = "AM";
    if (chart.birth_time) {
      const [h24s, ms] = chart.birth_time.split(":");
      const h24 = parseInt(h24s, 10);
      ampm = h24 >= 12 ? "PM" : "AM";
      const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
      hour = String(h12).padStart(2, "0");
      minute = ms;
    }
    setter({
      customLabel: chart.label || "",
      date: chart.birth_date, place: chart.birth_place,
      lat: chart.birth_lat, lon: chart.birth_lon,
      hour, minute, ampm,
    });
  };

  const pickSaved = (setter) => (id) => {
    const c = saved.find(x => x.chart_id === id);
    if (c) applyChartToForm(c, setter);
  };

  const canSave = (p) => !!(p.customLabel.trim() && p.date && p.lat != null && p.lon != null);

  const savePerson = async (p) => {
    if (!canSave(p)) { toast.error("Give them a nickname, date, and picked place."); return; }
    let birth_time = null;
    if (p.hour && p.minute) {
      let h = parseInt(p.hour, 10);
      if (p.ampm === "AM") h = (h === 12 ? 0 : h); else h = (h === 12 ? 12 : h + 12);
      birth_time = `${String(h).padStart(2, "0")}:${p.minute}`;
    }
    try {
      const r = await api.saveChart({
        label: p.customLabel.trim(), birth_date: p.date, birth_time,
        birth_place: p.place, birth_lat: p.lat, birth_lon: p.lon,
      });
      setSaved(s => [r.chart, ...s]);
      toast.success(`Saved ${r.chart.label}`);
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === "string" ? det : (det?.message || "Couldn't save."));
    }
  };

  const run = async () => {
    if (!ready) { toast.error("Fill in birth date + place for both people (times optional but recommended)."); return; }
    setBusy(true); setData(null);
    try {
      const r = await api.synastry({
        person_a: synastryPersonToPayload(you, "You"),
        person_b: synastryPersonToPayload(them, "Them"),
      });
      setData(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate the synastry reading.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="synastry-view" className="text-veil">
      <p className="text-[#1A1A1A] font-semibold mb-5">
        Synastry compares two full birth charts to find the real dynamics between you — attraction, communication, friction, long-term glue. Times aren&apos;t required but they unlock Rising and house placements for an even richer read.
      </p>

      <div className="grid md:grid-cols-2 gap-5">
        <SynastryPersonCard label="You" testidPrefix="syn-you" data={you} setData={setYou}
          canSave={canSave(you)} onSave={() => savePerson(you)}
          savedCharts={saved} onPickSaved={pickSaved(setYou)} />
        <SynastryPersonCard label="Them" testidPrefix="syn-them" data={them} setData={setThem}
          canSave={canSave(them)} onSave={() => savePerson(them)}
          savedCharts={saved} onPickSaved={pickSaved(setThem)} />
      </div>

      <button onClick={run} disabled={busy || !ready} data-testid="synastry-run"
        className="mt-6 px-7 py-4 rounded-full bg-[#10B981] text-white font-bold text-lg inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-60">
        <Heart className="w-5 h-5" /> {busy ? "Reading the stars…" : "Read our synastry"}
      </button>
      {busy && (
        <p className="mt-3 text-lg font-bold text-[#10B981]" data-testid="synastry-loading-notice">
          💞 Weaving your two charts together — this can take up to a minute. Please stay on this page.
        </p>
      )}

      {data && (
        <div className="mt-10 space-y-6" data-testid="synastry-result">
          <div className="flex items-center gap-3 flex-wrap">
            <DownloadBtn testid="synastry-download" onClick={() => synastryToDoc(data)} />
            <ShareSynastryBtn readingId={data.reading_id} />
          </div>
          <SynastryResultCards data={data} />
        </div>
      )}
    </div>
  );
}

function ShareSynastryBtn({ readingId }) {
  const [busy, setBusy] = useState(false);
  const share = async () => {
    if (!readingId) { toast.error("Generate a fresh reading to share it."); return; }
    setBusy(true);
    try {
      const r = await api.shareSynastry(readingId);
      const url = `${window.location.origin}${r.share_path}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Private link copied — send it to them!");
      } catch {
        window.prompt("Copy your private synastry link:", url);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create the share link.");
    } finally { setBusy(false); }
  };
  return (
    <button onClick={share} disabled={busy} data-testid="synastry-share-btn"
      className="px-6 py-3 rounded-full bg-[#10B981] text-white font-bold text-lg inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-60">
      <Share2 className="w-5 h-5" /> {busy ? "Creating link…" : "Share our Synastry"}
    </button>
  );
}

export function SynastryResultCards({ data }) {
  return (
    <>
      <div className="rounded-3xl border-2 border-[#FF1A82] bg-white/70 backdrop-blur-lg p-7 shadow-sm">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div className="text-xs uppercase tracking-[0.32em] font-bold text-[#FF1A82]">Overall</div>
          <div className="text-5xl font-heading font-bold text-[#1A1A1A]" data-testid="synastry-score">{data.score}<span className="text-2xl text-[#5A5A5A]">/100</span></div>
        </div>
        <p className="mt-2 font-heading text-2xl font-bold text-[#1A1A1A]">{data.headline}</p>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="rounded-2xl bg-white/70 backdrop-blur-lg border border-black/10 p-6 shadow-sm">
          <h3 className="font-heading text-xl font-bold text-[#10B981]">Attraction</h3>
          <p className="mt-2 text-[#1A1A1A] font-semibold leading-relaxed">{data.attraction}</p>
        </div>
        <div className="rounded-2xl bg-white/70 backdrop-blur-lg border border-black/10 p-6 shadow-sm">
          <h3 className="font-heading text-xl font-bold text-[#FF1A82]">Communication</h3>
          <p className="mt-2 text-[#1A1A1A] font-semibold leading-relaxed">{data.communication}</p>
        </div>
        <div className="rounded-2xl bg-white/70 backdrop-blur-lg border border-black/10 p-6 shadow-sm">
          <h3 className="font-heading text-xl font-bold text-[#10B981]">Friction</h3>
          <p className="mt-2 text-[#1A1A1A] font-semibold leading-relaxed">{data.friction}</p>
        </div>
        <div className="rounded-2xl bg-white/70 backdrop-blur-lg border border-black/10 p-6 shadow-sm">
          <h3 className="font-heading text-xl font-bold text-[#FF1A82]">Long-term glue</h3>
          <p className="mt-2 text-[#1A1A1A] font-semibold leading-relaxed">{data.long_term}</p>
        </div>
      </div>

      <div className="rounded-2xl bg-white/70 backdrop-blur-lg border border-black/10 p-6 shadow-sm">
        <h3 className="font-heading text-xl font-bold text-[#1A1A1A]">Advice</h3>
        <div className="mt-3"><Bullets items={data.advice} /></div>
      </div>
    </>
  );
}


function TarotView({ onGoNatal }) {
  const [question, setQuestion] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [spread, setSpread] = useState(3);
  const [status, setStatus] = useState(null);
  const [paying, setPaying] = useState(false);
  const [clarifying, setClarifying] = useState(false);

  const pullClarifier = async () => {
    setClarifying(true);
    try {
      const r = await api.tarotClarify(data.reading_id);
      setData(d => ({ ...d, clarifiers: [...(d.clarifiers || []), r.clarifier], clarifiers_allowed: r.clarifiers_allowed }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't pull a clarifying card.");
    } finally { setClarifying(false); }
  };
  const [history, setHistory] = useState([]);
  const [openReading, setOpenReading] = useState(null);
  const [mode, setMode] = useState("draw");
  const [catalog, setCatalog] = useState(null);
  const [layoutKey, setLayoutKey] = useState("ppf");
  const [manualCount, setManualCount] = useState(3);
  const [manualCards, setManualCards] = useState(Array.from({ length: 3 }, () => ({ name: "", orientation: "Upright" })));

  const setManualN = (n) => {
    setManualCount(n);
    setManualCards(prev => Array.from({ length: n }, (_, i) => prev[i] || { name: "", orientation: "Upright" }));
  };
  const setCard = (i, patch) => setManualCards(prev => prev.map((c, j) => j === i ? { ...c, ...patch } : c));

  const refreshStatus = () => {
    api.tarotStatus().then(setStatus).catch(() => {});
    api.tarotHistory().then(r => setHistory(r.readings || [])).catch(() => {});
  };
  useEffect(() => { refreshStatus(); api.tarotSpreads().then(setCatalog).catch(() => {}); api.evt("tarot_page_viewed"); }, []);

  // Handle return from Stripe checkout (?session_id=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (params.get("payment") === "cancelled") {
      toast.info("Payment cancelled — no charge was made.");
      window.history.replaceState({}, "", "/zodiac");
      return;
    }
    if (!sid) return;
    window.history.replaceState({}, "", "/zodiac");
    let tries = 0;
    setPaying(true);
    const poll = async () => {
      tries += 1;
      try {
        const r = await api.paymentStatus(sid);
        if (r.payment_status === "paid") {
          toast.success("Payment received! Your reading is unlocked below.");
          setPaying(false); refreshStatus(); return;
        }
        if (["failed", "expired"].includes(r.payment_status)) {
          toast.error("Payment didn't go through — you were not charged.");
          setPaying(false); return;
        }
      } catch {}
      if (tries < 10) setTimeout(poll, 2000);
      else { toast.error("Payment check timed out. Refresh the page in a moment."); setPaying(false); }
    };
    poll();
  }, []);

  const SPREADS = [
    { n: 3, label: "3 Cards", sub: "Past · Present · Future", price: 1.99 },
    { n: 6, label: "6 Cards", sub: "+ Advice · Outcome · Heart of It", price: 3.99 },
    { n: 9, label: "9 Cards", sub: "Deep dive", price: 5.99 },
    { n: 12, label: "12 Cards", sub: "The full picture", price: 7.99 },
  ];
  const freeAvailable = status && !status.free_used_today;
  const creditFor = (n) => status?.credits?.find(c => c.spread === n);
  const canRunNow = (n) => !!status?.unlimited || !!creditFor(n) || (freeAvailable && n === 3);
  const priceTag = (n) => {
    if (status?.unlimited) return "FREE — admin";
    if (creditFor(n)) return "Ready — paid";
    if (freeAvailable && n === 3) return "FREE today";
    return `$${SPREADS.find(s => s.n === n).price}`;
  };

  const pay = async () => {
    api.evt("checkout_started", { spread: String(spread) });
    setBusy(true);
    try {
      const r = await api.tarotCheckout(`tarot_spread_${spread}`, spread, window.location.origin);
      window.location.href = r.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout — try again.");
      setBusy(false);
    }
  };

  const run = async () => {
    if (question.trim().length < 5) { toast.error("Ask the cards a real question (5+ characters)."); return; }
    api.evt("reading_started", { spread: String(mode === "manual" ? manualCount : spread) });
    if (mode === "manual") {
      if (manualCards.some(c => !c.name)) { toast.error("Choose every card you pulled before reading."); return; }
      const names = manualCards.map(c => c.name);
      if (new Set(names).size !== names.length) { toast.error("You picked the same card twice — each card appears once in a deck."); return; }
      setBusy(true); setData(null);
      try {
        const r = await api.tarotManual(question.trim(), manualCards, layoutKey);
        setData(r);
        refreshStatus();
      } catch (e) {
        if (e?.response?.status === 402) { toast.error(e.response.data?.detail); refreshStatus(); }
        else toast.error(e?.response?.data?.detail || "The cards aren't cooperating — try again.");
      } finally { setBusy(false); }
      return;
    }
    if (!canRunNow(spread)) { pay(); return; }
    setBusy(true); setData(null);
    try {
      const r = await api.tarot(question.trim(), spread, layoutKey);
      setData(r);
      refreshStatus();
    } catch (e) {
      if (e?.response?.status === 402) { toast.error(e.response.data?.detail); refreshStatus(); }
      else toast.error(e?.response?.data?.detail || "The cards aren't cooperating — try again.");
    } finally { setBusy(false); }
  };

  const layoutOptions = (n) => catalog?.layouts?.[String(n)] || catalog?.layouts?.[n] || [];
  const activeLayouts = layoutOptions(mode === "manual" ? manualCount : spread);

  const gridCols = data && data.cards.length > 5 ? "sm:grid-cols-3 lg:grid-cols-4" : "sm:grid-cols-3";

  if (status && !status.natal_saved) {
    return (
      <div data-testid="tarot-view" className="text-veil">
        <div className="rounded-2xl bg-white/70 backdrop-blur-lg border-2 border-[#FF1A82] p-6 sm:p-8 text-center" data-testid="tarot-natal-gate">
          <Sparkles className="w-8 h-8 text-[#10B981] mx-auto" />
          <h3 className="mt-3 font-heading text-2xl font-bold text-[#1A1A1A]">Your natal chart comes first</h3>
          <p className="mt-2 text-[#1A1A1A] leading-relaxed max-w-lg mx-auto">
            For an accurate reading, the cards need to know <span className="font-semibold text-[#FF1A82]">you</span> — your Sun, Moon and Rising.
            Complete your natal chart once and every tarot reading will be tailored to your exact chart.
          </p>
          <button onClick={onGoNatal} data-testid="tarot-goto-natal"
            className="mt-5 px-7 py-3.5 rounded-full bg-[#FF1A82] text-white font-bold text-lg hover:brightness-110">
            Create my natal chart
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="tarot-view" className="text-veil">
      <p className="text-[#1A1A1A] font-semibold mb-2">Ask the cards anything — love, closure, a decision you’re sitting on. Cards are drawn at random and read against your exact question.</p>
      <p className="text-sm text-[#666666] mb-5" data-testid="tarot-daily-note">
        {status == null ? "" : freeAvailable
          ? "Your free 3-card reading for today is available. Want more depth? Bigger spreads are $3.99–$7.99."
          : "You've used today's free reading. Readings: $1.99 (3 cards) up to $7.99 (12 cards)."}
      </p>

      <div className="mb-6 inline-flex rounded-2xl border-2 border-black/15 overflow-hidden" data-testid="tarot-mode-toggle">
        <button type="button" onClick={() => { setMode("draw"); setLayoutKey("ppf"); setData(null); }} data-testid="tarot-mode-draw"
          className={`px-6 py-3 font-bold text-lg ${mode === "draw" ? "bg-[#10B981] text-white" : "bg-white/70 backdrop-blur-lg text-[#5A5A5A]"}`}>Draw for me</button>
        <button type="button" onClick={() => { setMode("manual"); setLayoutKey("ppf"); setData(null); }} data-testid="tarot-mode-manual"
          className={`px-6 py-3 font-bold text-lg ${mode === "manual" ? "bg-[#FF1A82] text-white" : "bg-white/70 backdrop-blur-lg text-[#5A5A5A]"}`}>I pulled my own cards</button>
      </div>

      {mode === "draw" && (
        <>
          <label className="block text-xs uppercase tracking-[0.22em] text-[#10B981] font-bold">Choose your spread</label>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="tarot-spread-picker">
            {SPREADS.map(s => (
              <button key={s.n} onClick={() => { setSpread(s.n); setLayoutKey("ppf"); }} data-testid={`tarot-spread-${s.n}`}
                className={`rounded-2xl border-2 p-4 text-left transition-colors ${spread === s.n ? "border-[#10B981] bg-white/70 backdrop-blur-lg" : "border-black/10 bg-white/60 hover:border-[#10B981]/50"}`}>
                <div className="font-heading text-xl font-bold text-[#1A1A1A]">{s.label}</div>
                <div className="text-xs text-[#666666] mt-0.5">{s.sub}</div>
                <div className={`text-sm font-bold mt-2 ${canRunNow(s.n) ? "text-[#FF1A82]" : "text-[#10B981]"}`}>{status ? priceTag(s.n) : "…"}</div>
              </button>
            ))}
          </div>
        </>
      )}

      {mode === "manual" && (
        <div data-testid="tarot-manual-section">
          <p className="text-lg text-[#1A1A1A] font-semibold mb-3">Pulled cards from your own physical deck? Enter them below (up to 11) and the reading will interpret <em>your</em> exact spread.</p>
          <label className="block text-xs uppercase tracking-[0.22em] text-[#FF1A82] font-bold">How many cards did you pull?</label>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="tarot-manual-count">
            {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
              <button key={n} onClick={() => { setManualN(n); setLayoutKey("ppf"); }} data-testid={`tarot-manual-count-${n}`}
                className={`w-11 h-11 rounded-full font-bold text-lg border-2 transition-colors ${manualCount === n ? "border-[#FF1A82] bg-[#FF1A82] text-white" : "border-black/15 bg-white/70 text-[#1A1A1A] hover:border-[#FF1A82]"}`}>{n}</button>
            ))}
          </div>
          <div className="mt-4 space-y-2" data-testid="tarot-manual-cards">
            {manualCards.map((c, i) => (
              <div key={i} className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <span className="text-lg font-bold text-[#5A5A5A] w-40 shrink-0">
                  {(activeLayouts.find(l => l.key === layoutKey)?.positions?.[i]) || `Card ${i + 1}`}
                </span>
                <select value={c.name} onChange={e => setCard(i, { name: e.target.value })} data-testid={`tarot-card-select-${i}`}
                  className="flex-1 bg-white/70 backdrop-blur-lg border border-black/15 rounded-xl px-3 py-2.5 text-lg text-[#1A1A1A] font-semibold outline-none focus:border-[#FF1A82]">
                  <option value="">— choose the card —</option>
                  {(catalog?.deck || []).map(name => <option key={name} value={name}>{name}</option>)}
                </select>
                <button type="button" onClick={() => setCard(i, { orientation: c.orientation === "Upright" ? "Reversed" : "Upright" })}
                  data-testid={`tarot-card-orient-${i}`}
                  className={`px-4 py-2.5 rounded-xl font-bold text-lg border-2 transition-colors ${c.orientation === "Reversed" ? "border-[#10B981] text-[#10B981] bg-[#10B981]/10" : "border-[#FF1A82] text-[#FF1A82] bg-[#FF1A82]/10"}`}>
                  {c.orientation}
                </button>
              </div>
            ))}
          </div>
          <p className="mt-3 text-sm text-[#666666]" data-testid="tarot-manual-note">
            Named spread styles are available at 3, 5, 7 and 10 cards — other counts use simple Card 1…N positions.
            You get <strong>1 free reading of your own cards every day</strong> — separate from your free drawn reading. After that, a purchased credit covers it.
          </p>
        </div>
      )}

      {activeLayouts.length > 1 && (
        <>
          <label className="mt-6 block text-xs uppercase tracking-[0.22em] text-[#10B981] font-bold">Choose your spread style</label>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="tarot-layout-picker">
            {activeLayouts.map(l => (
              <button key={l.key} onClick={() => setLayoutKey(l.key)} data-testid={`tarot-layout-${l.key}`}
                className={`px-4 py-2.5 rounded-full font-bold text-lg border-2 transition-colors ${layoutKey === l.key ? "border-[#10B981] bg-[#10B981] text-white" : "border-black/15 bg-white/70 text-[#1A1A1A] hover:border-[#10B981]/60"}`}>
                {l.label}
              </button>
            ))}
          </div>
          <p className="mt-2 text-sm text-[#666666]" data-testid="tarot-layout-positions">
            {(activeLayouts.find(l => l.key === layoutKey) || activeLayouts[0]).positions.join(" · ")}
          </p>
        </>
      )}

      <label className="mt-6 block text-xs uppercase tracking-[0.22em] text-[#10B981] font-bold">Your question</label>
      <textarea value={question} onChange={e => setQuestion(e.target.value)} maxLength={300} rows={3}
        placeholder="e.g. Should I give him another chance?"
        data-testid="tarot-question"
        className="mt-2 w-full bg-white/70 backdrop-blur-lg border border-black/10 rounded-2xl px-4 py-3 text-[#1A1A1A] outline-none focus:border-[#10B981] font-semibold resize-none" />
      <button onClick={run} disabled={busy || paying || status == null} data-testid="tarot-run"
        className="mt-4 px-7 py-4 rounded-full bg-[#10B981] text-white font-bold text-lg inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-60">
        <Sparkles className="w-5 h-5" />
        {paying ? "Confirming payment…" : busy ? (mode === "manual" ? "Reading your spread…" : "Pulling your cards…") :
          mode === "manual" ? `Read my ${manualCount} card${manualCount > 1 ? "s" : ""}` :
          canRunNow(spread) ? `Pull my ${spread} cards` :
          `Buy ${spread}-card reading — $${SPREADS.find(s => s.n === spread).price}`}
      </button>
      {busy && (
        <p className="mt-3 text-lg font-bold text-[#10B981]" data-testid="tarot-loading-notice">
          🔮 The cards are speaking — this can take up to a minute. Please stay on this page.
        </p>
      )}

      {data && (
        <div className="mt-10 space-y-6" data-testid="tarot-result">
          <DownloadBtn testid="tarot-download" onClick={() => tarotToDoc(data)} />
          <div className={`grid ${gridCols} gap-5`}>
            {data.cards.map((c, i) => (
              <div key={c.position} className={`rounded-2xl border-2 p-6 shadow-sm bg-white/70 backdrop-blur-lg ${c.orientation === "Reversed" ? "border-[#10B981]" : "border-[#FF1A82]"}`} data-testid={`tarot-card-${i}`}>
                <div className="text-xs uppercase tracking-[0.28em] font-bold text-[#5A5A5A]">{c.position}</div>
                <div className="mt-2 font-heading text-2xl font-bold text-[#1A1A1A]">{c.name}</div>
                <div className={`mt-1 font-bold ${c.orientation === "Reversed" ? "text-[#10B981]" : "text-[#FF1A82]"}`}>{c.orientation}</div>
                {data.card_readings?.[i] && <p className="mt-3 text-[#1A1A1A] font-semibold leading-relaxed">{data.card_readings[i]}</p>}
              </div>
            ))}
          </div>
          <div className="rounded-2xl bg-white/70 backdrop-blur-lg border-2 border-[#10B981] p-6 shadow-sm">
            <h3 className="font-heading text-2xl font-bold text-[#1A1A1A]">The cards’ answer</h3>
            <p className="mt-3 text-[#1A1A1A] font-semibold leading-relaxed">{data.overall}</p>
          </div>
          <div className="rounded-2xl bg-white/70 backdrop-blur-lg border border-black/10 p-6 shadow-sm">
            <h3 className="font-heading text-xl font-bold text-[#FF1A82]">Guidance</h3>
            <div className="mt-3"><Bullets items={data.guidance} /></div>
          </div>

          {(data.clarifiers || []).map((c, i) => (
            <div key={i} className="rounded-2xl bg-white/70 backdrop-blur-lg border-2 border-[#FF1A82]/50 p-6 shadow-sm" data-testid={`clarifier-${i}`}>
              <h3 className="font-heading text-xl font-bold text-[#FF1A82]">✨ Clarifying card: {c.card.name} {c.card.orientation === "Reversed" ? "(Reversed)" : ""}</h3>
              <p className="mt-3 text-[#1A1A1A] font-semibold leading-relaxed">{c.text}</p>
            </div>
          ))}
          {data.reading_id && (data.clarifiers || []).length < (data.clarifiers_allowed || 0) && (
            <button onClick={pullClarifier} disabled={clarifying} data-testid="pull-clarifier-btn"
              className="w-full px-6 py-4 rounded-2xl bg-[#FF1A82] text-white text-lg font-bold hover:brightness-110 transition disabled:opacity-60">
              {clarifying ? "Pulling your clarifying card…" :
                `Pull a FREE clarifying card (${(data.clarifiers_allowed || 0) - (data.clarifiers || []).length} left)`}
            </button>
          )}
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-12" data-testid="tarot-history">
          <h3 className="font-heading text-2xl font-bold text-[#1A1A1A]">My past readings</h3>
          <div className="mt-4 space-y-3">
            {history.map(h => (
              <div key={h.reading_id} className="rounded-2xl bg-white/70 backdrop-blur-lg border border-black/10 shadow-sm">
                <button onClick={() => setOpenReading(openReading === h.reading_id ? null : h.reading_id)}
                  data-testid={`tarot-history-${h.reading_id}`}
                  className="w-full text-left px-5 py-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[#1A1A1A] font-semibold truncate">“{h.question}”</div>
                    <div className="text-sm text-[#666666] mt-0.5">
                      {new Date(h.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                      &nbsp;·&nbsp;{h.spread === 10 ? "Celtic Cross" : `${h.spread} cards`}{h.paid ? " · paid" : ""}
                    </div>
                  </div>
                  <span className={`text-[#10B981] font-bold shrink-0 transition-transform ${openReading === h.reading_id ? "rotate-90" : ""}`}>›</span>
                </button>
                {openReading === h.reading_id && (
                  <div className="px-5 pb-5 space-y-3">
                    {h.cards?.map((c, i) => (
                      <div key={i}>
                        <span className={`font-bold ${c.orientation === "Reversed" ? "text-[#10B981]" : "text-[#FF1A82]"}`}>{c.position}: {c.name} ({c.orientation})</span>
                        {h.card_readings?.[i] && <p className="text-[#1A1A1A] leading-relaxed">{h.card_readings[i]}</p>}
                      </div>
                    ))}
                    {h.overall && <p className="text-[#1A1A1A] font-semibold leading-relaxed border-t border-black/10 pt-3">{h.overall}</p>}
                    <DownloadBtn testid={`tarot-history-download-${h.reading_id}`}
                      onClick={() => tarotToDoc(h)} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


export default function ZodiacPage() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState(() =>
    new URLSearchParams(window.location.search).has("session_id") ||
    new URLSearchParams(window.location.search).has("payment") ? "tarot" : "natal");
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "smooth" }); }, [tab]);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/zodiac", { replace: true }); }
  }, [user, loading, nav]);

  if (loading) return <PageBg><Nav /><div className="max-w-2xl mx-auto px-6 py-20 text-center text-[#3A3A3A]">Checking session…</div></PageBg>;
  if (!user) return null;

  return (
    <PageBg poster={POSTER_P4_ZODIAC} scrim="bg-white/80">
      <Nav />
      <section className="max-w-4xl mx-auto px-6 py-14" data-testid="zodiac-page">
        <div className="text-veil">
        <div className="text-sm uppercase tracking-[0.32em] font-bold mb-3"><span className="text-[#FF1A82]">Welp</span> <span className="text-[#5A5A5A]">·</span> Zodiac</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[#1A1A1A]">
          The stars between you. <Heart className="inline w-10 h-10 text-[#10B981] fill-[#10B981]" />
        </h1>
        <p className="mt-4 text-[#1A1A1A] font-semibold text-lg">Know yourself with a full natal chart, read the real dynamics between you and them with synastry, or ask the cards directly. Separate from reviews.</p>
        </div>

        <div className="mt-8 flex gap-3 flex-wrap">
          <Tab active={tab === "natal"} onClick={() => setTab("natal")} label="My Natal Chart" testid="tab-natal" />
          <Tab active={tab === "synastry"} onClick={() => setTab("synastry")} label="Synastry (You + Them)" testid="tab-synastry" />
          <Tab active={tab === "tarot"} onClick={() => setTab("tarot")} label="Tarot" testid="tab-tarot" />
        </div>

        <div className="mt-8">
          {tab === "natal" ? <NatalView /> : tab === "synastry" ? <SynastryView /> : <TarotView onGoNatal={() => setTab("natal")} />}
        </div>
      </section>
    </PageBg>
  );
}
